class AACartProgress extends HTMLElement {
  static observedAttributes = [
    'data-total',
    'data-free-threshold',
    'data-bonus-threshold',
    'data-bonus-label',
    'data-sg',
    'data-rate-known',
    'data-show-currency-code',
  ];

  #rateRetry;
  #rateRetryCount = 0;
  #renderQueued = false;
  #markupObserver;

  connectedCallback() {
    this.syncMarkupObserver();
    this.queueRender();
  }

  disconnectedCallback() {
    clearTimeout(this.#rateRetry);
    this.#markupObserver?.disconnect();
  }

  syncMarkupObserver() {
    if (this.dataset.rateKnown === 'true') {
      this.#markupObserver?.disconnect();
      return;
    }

    this.observeMarkup();
  }

  observeMarkup() {
    if (typeof MutationObserver === 'undefined') return;

    this.#markupObserver ??= new MutationObserver(() => this.queueRender());
    this.#markupObserver.observe(this, { attributes: true, childList: true, subtree: true });
  }

  attributeChangedCallback(name) {
    if (!this.isConnected) return;

    if (name === 'data-rate-known') this.syncMarkupObserver();
    if (this.#renderQueued) return;

    // Cart morphs update several attributes and their child markup together.
    // Render once after that synchronous batch has completed.
    this.queueRender();
  }

  queueRender() {
    if (this.#renderQueued) return;

    this.#renderQueued = true;
    queueMicrotask(() => {
      this.#renderQueued = false;
      if (this.isConnected) this.render();
    });
  }

  get rate() {
    const rate = parseFloat(window.Shopify?.currency?.rate);
    return Number.isFinite(rate) && rate > 0 ? rate : null;
  }

  retryWhenCurrencyIsReady() {
    if (this.#rateRetryCount >= 20) return;

    clearTimeout(this.#rateRetry);
    this.#rateRetryCount += 1;
    this.#rateRetry = setTimeout(() => this.render(), 250);
  }

  money(cents) {
    const currency = window.Shopify?.currency?.active || 'SGD';
    const amount = Math.max(0, cents) / 100;

    try {
      // 'en-SG' is pinned (house rule: request.locale is bare `en` and formats American);
      // default currency display so it reads like the threshold above it: "MYR 47.30", "US$12.00".
      const opts = { style: 'currency', currency, minimumFractionDigits: 2 };
      let formatted = new Intl.NumberFormat('en-SG', opts).format(amount);
      // Same rule as aa-offer-amounts: ISO-code output ("MYR 47.30") becomes the local symbol ("RM47.30").
      if (formatted.includes(currency)) {
        formatted = new Intl.NumberFormat('en-SG', { ...opts, currencyDisplay: 'narrowSymbol' })
          .format(amount)
          .replace(/^(\D+?)\s+(?=\d)/, '$1');
      }

      return this.dataset.showCurrencyCode === 'true' ? `${formatted} ${currency}` : formatted;
    } catch (error) {
      return `${currency} ${amount.toFixed(2)}`;
    }
  }

  render() {
    const rateKnown = this.dataset.rateKnown === 'true';
    const rate = rateKnown ? 1 : this.rate;

    // Liquid already renders shop-currency carts accurately. Presentment
    // currency carts deliberately start unresolved and are hydrated here.
    if (rateKnown) {
      this.syncMarkupObserver();
      return;
    }
    if (rate === null) {
      this.retryWhenCurrencyIsReady();
      return;
    }

    clearTimeout(this.#rateRetry);

    const isSG = this.dataset.sg === 'true';
    const total = Number(this.dataset.total) || 0;
    // Same rounding as the printed text: up to a whole unit of this currency
    // (snippets/aa-offer-amounts.liquid), never under-promise.
    const whole = (cents) => Math.ceil((Number(cents) / 100) * rate - 1e-9) * 100;
    const free = whole(this.dataset.freeThreshold);
    const bonus = whole(this.dataset.bonusThreshold);
    const label = this.dataset.bonusLabel || '10% off';
    // Copy templates from Theme settings (snippets/cart-progress-bar.liquid).
    const fillIn = (template, amount) =>
      template.replaceAll('[amount]', amount ?? '').replaceAll('[reward]', label);
    const { txtToFree = '', txtToBonus = '', txtGotBoth = '', txtGotBonus = '' } = this.dataset;

    const message = this.querySelector('[data-aa-message]');
    const freeUnlocked = this.querySelector('[data-aa-free-unlocked]');
    const fill = this.querySelector('[data-aa-fill]');
    const track = this.querySelector('[data-aa-track]');
    if (!message || !fill || !track) return;

    this.#markupObserver?.disconnect();
    try {
      const check = this.querySelector('.cart-progress__check');
      const tick = check ? check.outerHTML : '';

      if (freeUnlocked) {
        freeUnlocked.hidden = !(isSG && total >= free && total < bonus);
      }

      if (isSG) {
        if (total >= bonus) {
          message.innerHTML = tick + fillIn(txtGotBoth);
        } else if (total >= free) {
          message.innerHTML = fillIn(txtToBonus, this.money(bonus - total));
        } else {
          message.innerHTML = fillIn(txtToFree, this.money(free - total));
        }
      } else if (total >= bonus) {
        message.innerHTML = tick + fillIn(txtGotBonus);
      } else {
        message.innerHTML = fillIn(txtToBonus, this.money(bonus - total));
      }

      // Same segments as the Liquid: [start, end] per rung, in this currency.
      const spans = { free: [0, free], bonus: [isSG ? free : 0, bonus] };
      // The first unfinished segment is the next rung: its remainder is amber.
      let freeDone = true;
      let nextFound = false;
      for (const segFill of this.querySelectorAll('[data-aa-fill]')) {
        const [start, end] = spans[segFill.dataset.aaFill] || spans.bonus;
        let pct = end > start ? Math.min(100, Math.max(0, ((total - start) * 100) / (end - start))) : 0;
        if (segFill.dataset.aaFill === 'free') freeDone = pct >= 100;
        else if (isSG && !freeDone && pct < 100) pct = 0;
        segFill.style.width = `${pct}%`;
        const isNext = !nextFound && pct < 100;
        if (isNext) nextFound = true;
        segFill.parentElement.classList.toggle('cart-progress__seg--next', isNext);
      }

      track.setAttribute('aria-valuemax', String(bonus / 100));
      track.setAttribute('aria-valuenow', String(total / 100));

      this.classList.toggle('cart-progress--free-reached', isSG && total >= free);
      this.classList.toggle('cart-progress--bonus-reached', total >= bonus);
    } finally {
      if (this.isConnected) this.syncMarkupObserver();
    }
  }
}

if (!customElements.get('aa-cart-progress')) {
  customElements.define('aa-cart-progress', AACartProgress);
}
